/**
 * Les formats que ces champs ont **dans la vraie vie** (#1491).
 *
 * Un IBAN se lit par groupes de quatre, un numéro de sécurité sociale par ses sept segments : c'est
 * ainsi qu'ils sont imprimés sur une attestation, une carte Vitale ou un RIB, et donc ainsi qu'on
 * les relit pour vérifier. Servis d'un bloc — `FR7612406001248002338866219` —, ils obligent à
 * compter les chiffres du doigt, et une erreur de saisie y est invisible.
 *
 * **Deux valeurs, jamais confondues.** La forme *lue* porte des espaces ; la forme *envoyée* n'en a
 * aucun. C'est ce que l'API attend — `ibanSalary` est plafonné à 34 caractères, soit la longueur
 * maximale d'un IBAN **sans** séparateurs : les espaces y mangeraient le champ.
 */

/** L'IBAN tel qu'on l'envoie : majuscules, sans séparateur, 34 caractères au plus. */
export const normalizeIban = (input: string): string =>
  input
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 34);

/** L'IBAN tel qu'on le lit : groupes de quatre. */
export const formatIban = (input: string): string =>
  (normalizeIban(input).match(/.{1,4}/g) ?? []).join(' ');

/**
 * Le numéro de sécurité sociale tel qu'on l'envoie : 15 caractères, sans espaces.
 *
 * Les lettres **A et B** sont admises : le département de naissance vaut `2A` ou `2B` pour la
 * Corse. Les refuser rendrait le champ inutilisable pour les personnes nées là-bas — un cas rare
 * qu'on ne découvre qu'en production, sur une fiche qu'on ne peut plus enregistrer.
 */
export const normalizeNir = (input: string): string =>
  input
    .toUpperCase()
    .replace(/[^0-9AB]/g, '')
    .slice(0, 15);

/**
 * Le numéro tel qu'il est imprimé : `1 23 45 67 890 123 45`.
 *
 * Les sept segments ont chacun un sens — sexe, année, mois, département, commune, ordre, clé — et
 * c'est segment par segment qu'on recopie un numéro depuis une attestation.
 */
const NIR_GROUPS = [1, 2, 2, 2, 3, 3, 2];

export const formatNir = (input: string): string => {
  const raw = normalizeNir(input);
  const parts: string[] = [];
  let at = 0;
  for (const size of NIR_GROUPS) {
    if (at >= raw.length) break;
    parts.push(raw.slice(at, at + size));
    at += size;
  }
  return parts.join(' ');
};

export type FieldFormat = 'iban' | 'nir';

export const normalizeBy = (format: FieldFormat, input: string): string =>
  format === 'iban' ? normalizeIban(input) : normalizeNir(input);

export const formatBy = (format: FieldFormat, input: string): string =>
  format === 'iban' ? formatIban(input) : formatNir(input);

/**
 * Où poser le curseur après reformatage.
 *
 * Sans cela, corriger un chiffre au milieu d'un IBAN renvoie le curseur à la fin à chaque frappe —
 * et la correction devient impossible. On compte les caractères **significatifs** avant le curseur,
 * puis on retrouve leur position dans la chaîne espacée.
 */
export const caretAfterFormat = (formatted: string, significantBefore: number): number => {
  if (significantBefore <= 0) return 0;
  let seen = 0;
  for (let index = 0; index < formatted.length; index++) {
    if (formatted[index] !== ' ') seen++;
    if (seen === significantBefore) return index + 1;
  }
  return formatted.length;
};

/** Combien de caractères significatifs précèdent le curseur, dans la chaîne telle qu'affichée. */
export const significantBefore = (displayed: string, caret: number): number =>
  displayed.slice(0, caret).replace(/\s/g, '').length;
